import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AlertService, DialogType, MessageSeverity } from '../../../../services/alert.service';
import { UserCentreBrick, UserCentreBricks, UserCentreBricksService } from './user-centre-bricks.service';

function today(): string {
  const d = new Date();
  return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
}

/**
 * Centre bricks of one user (Users Management): where his business trips start (the From of a matrix expense).
 * Several can be open at once; closing one keeps it in the history below.
 */
@Component({
  selector: 'user-centre-bricks',
  templateUrl: './user-centre-bricks.component.html',
  styleUrls: ['./user-centre-bricks.component.css']
})
export class UserCentreBricksComponent implements OnChanges {
  @Input() userId: number;
  @Input() canManage = false;
  /** Emits the number of open centres after a change (the users table shows it). */
  @Output() changed = new EventEmitter<number>();

  data: UserCentreBricks;
  options: { id: number; name: string }[] = [];
  loading = false;
  saving = false;
  newBrickId: number = null;
  newStart = today();
  closing: UserCentreBrick = null;
  closeDate = today();

  constructor(private service: UserCentreBricksService, private alertService: AlertService) { }

  ngOnChanges() {
    if (!this.userId) return;
    this.load();
    this.service.options(this.userId).subscribe(o => this.options = o || []);
  }

  get openLinks(): UserCentreBrick[] { return (this.data && this.data.links || []).filter(x => x.isOpen); }
  get closedLinks(): UserCentreBrick[] { return (this.data && this.data.links || []).filter(x => !x.isOpen); }
  /** No centre of his own (set here or in his business unit): the territories' centres under him are used. */
  get inherits(): boolean { return !!this.data && !this.openLinks.length && !this.data.businessUnitCentres.length; }

  isUpcoming(link: UserCentreBrick): boolean { return link.startDate.substring(0, 10) > today(); }

  load() {
    this.loading = true;
    this.service.forUser(this.userId).subscribe(d => { this.data = d; this.loading = false; },
      () => { this.loading = false; });
  }

  private done() {
    this.saving = false;
    this.closing = null;
    this.service.forUser(this.userId).subscribe(d => { this.data = d; this.changed.emit(this.openLinks.filter(x => !this.isUpcoming(x)).length); });
  }

  private failed(err: any) {
    this.saving = false;
    const msg = err && err.error && (err.error.Message || err.error.message || (typeof err.error === 'string' ? err.error : null));
    this.alertService.showMessage('', msg || 'Failed to save.', MessageSeverity.Error);
  }

  add() {
    if (!this.newBrickId || !this.newStart) return;
    this.saving = true;
    this.service.add(this.userId, this.newBrickId, this.newStart).subscribe(() => { this.newBrickId = null; this.done(); }, e => this.failed(e));
  }

  startClose(link: UserCentreBrick) { this.closing = link; this.closeDate = today(); }

  saveClose() {
    if (!this.closing || !this.closeDate) return;
    this.saving = true;
    this.service.close(this.closing.id, this.closeDate).subscribe(() => this.done(), e => this.failed(e));
  }

  remove(link: UserCentreBrick) {
    this.alertService.showDialog('Delete the centre brick ' + link.brickName + '? Use Close instead to keep it in the history.', DialogType.Confirm, () => {
      this.saving = true;
      this.service.remove(link.id).subscribe(() => this.done(), e => this.failed(e));
    });
  }
}
